/**
 * نشانگرهای اعلان روی وب — قرینه‌ی
 * apps/mobile/src/features/notifications/useNotifIndicators.ts.
 *
 * سه لایه، از تندترین به کندترین:
 *
 *   ۱. **کانالِ Realtime** — همان لحظه که ردیفی برایت درج می‌شود. تا وقتی
 *      تب باز است، این همه‌ی کار را می‌کند.
 *   ۲. **اعلانِ مرورگر** (پوش) — وقتی تب بسته است. نگاه کن به `webPush.ts`.
 *   ۳. **نظرسنجی** — توری ایمنی، برای وقتی یکی از آن دو گم شود.
 *
 * ═══ ⚠ چرا فاصله‌ی نظرسنجی از ۲۰ ثانیه به دقیقه رفت ═══
 *
 * شرحِ کاملش — با عددهای هر دو اپ — در `core/lib/notifPolling.ts` است، چون
 * حالا موبایل هم از همان `pollInterval()` استفاده می‌کند. خلاصه‌اش: ۲۰
 * ثانیه توری ایمنی نبود، خودش بارِ اصلی بود؛ یک تبِ رهاشده در یک شب ۳۵۹۵
 * بار پرسید.
 *
 * دو تغییر، هر دو لازم:
 *   • فاصله از `pollInterval()` می‌آید — ۱۵ دقیقه اگر پوش روشن باشد، ۵
 *     دقیقه اگر نه. کسی که پوش ندارد تنها راهش همین است، پس تندتر.
 *   • `refetchIntervalInBackground: false` صریح نوشته شده تا تبِ پنهان
 *     اصلاً نپرسد. پیش‌فرضِ react-query هم همین است، ولی چیزی که یک شب
 *     ۱۷۰ کوئری در ساعت زد، ارزشِ صریح نوشتن دارد.
 *
 * روی وب زنگوله یا آیکونِ اپ نداریم؛ معادلش عنوانِ برگه‌ی مرورگر است —
 * همان الگویی که جیمیل/اسلک با «(3) Inbox» دارند.
 */
import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationRepo } from '@/core/data/miscRepo';
import { DEFAULT_PREFS, notifPrefsRepo } from '@/core/data/notifPrefsRepo';
import { useSession } from '@/core/session';
import { pollInterval } from '@/core/lib/notifPolling';
import { brand } from '@/core/lib/i18n';
import type { NotifCategory } from '@/core/domain/types';
import { emitFreshNotification } from './ForegroundNotices';
import { pushPermission } from './webPush';

/**
 * عنوانِ تب وقتی اعلانِ خوانده‌نشده هست: «(۳) نامِ برند».
 *
 * تابع است نه ثابت: `setBrandName` در بوت اجرا می‌شود و یک ثابتِ سطحِ ماژول
 * می‌تواند پیش از آن ارزیابی شود. صدا زدنش سرِ استفاده این ترتیب را بی‌اهمیت
 * می‌کند — و روی برندِ دوم (outami.com) همان نامی را می‌دهد که صفحه نشان
 * می‌دهد، نه نامِ پیش‌فرض.
 */
const baseTitle = (): string => brand();

export interface NotifIndicators {
  dots: Record<NotifCategory, boolean>;
  total: number;
}

export function useNotifIndicators(): NotifIndicators {
  const { profile } = useSession();
  const qc = useQueryClient();

  const counts = useQuery({
    queryKey: ['unreadByCategory'],
    queryFn: () => notificationRepo.unreadByCategory(profile!.id),
    enabled: !!profile,
    refetchInterval: pollInterval(pushPermission() === 'granted'),
    refetchIntervalInBackground: false,
  });

  const prefs = useQuery({
    queryKey: ['notifPrefs'],
    queryFn: notifPrefsRepo.mine,
    enabled: !!profile,
  });

  // کانالِ زنده: هر INSERT روی ردیفِ خودم، بی‌درنگ شمارنده رو تازه می‌کنه.
  // خودِ اشتراک در `notificationRepo` است چون موبایل هم همین را می‌خواهد.
  // ⚠ کانال یکی به‌ازای هر کاربر است، پس اعلانکِ داخلِ صفحه هم از همین
  // یک اشتراک تغذیه می‌شود (ForegroundNotices)، نه از اشتراکِ دومی که
  // اولی را می‌کُشت.
  useEffect(() => {
    if (!profile) return undefined;
    return notificationRepo.subscribeNew(profile.id, (fresh) => {
      qc.invalidateQueries({ queryKey: ['unreadByCategory'] });
      if (fresh) emitFreshNotification(fresh);
    });
  }, [profile, qc]);

  const n = counts.data ?? { friend_request: 0, signal: 0, event: 0 };
  const p = prefs.data ?? DEFAULT_PREFS;
  const shown = (k: NotifCategory) => (p[k] === 'off' ? 0 : n[k]);

  const dots: Record<NotifCategory, boolean> = {
    friend_request: shown('friend_request') > 0,
    signal: shown('signal') > 0,
    event: shown('event') > 0,
  };
  const total = shown('friend_request') + shown('signal') + shown('event');

  // عنوانِ برگه — تنها جایی که وقتی تبِ سایت پس‌زمینه‌ست هم دیده می‌شه.
  useEffect(() => {
    document.title = total > 0 ? `(${total}) ${baseTitle()}` : baseTitle();
  }, [total]);

  return { dots, total };
}
